import { useState, useEffect } from "react";
import {
  getSprintResumo, getSprintDevs, getSprintHistorico, getSprintDados,
} from "../api/sprint.api";
import { getSetupSprint } from "../api/setup.api";

const FECHADOS_STATUS = new Set([
  "Fechado", "Resolvido", "Reteste", "Em Teste", "Feedback",
  "Aguardando Testes", "Aguardando Versão", "Ajuste Especificação",
]);

const devColor = p => p >= 70 ? "#4ade80" : p >= 40 ? "#fbbf24" : "#f87171";

export default function SprintRetrospectiva() {
  const [dados,   setDados]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      getSprintResumo(),
      getSprintDevs(),
      getSprintHistorico(),
      getSprintDados(),
      getSetupSprint(),
    ]).then(([r, d, h, t, s]) => {
      setDados({
        resumo:   r.status === "fulfilled" ? r.value : null,
        devs:     d.status === "fulfilled" ? (d.value.developers || []) : [],
        historico: h.status === "fulfilled" ? (Array.isArray(h.value) ? h.value : []) : [],
        tickets:  t.status === "fulfilled" ? t.value : [],
        sprint:   s.status === "fulfilled" ? s.value : null,
      });
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh", color: "#475569" }}>
      Carregando retrospectiva...
    </div>
  );

  const { resumo, devs, historico, tickets, sprint } = dados;
  if (!resumo?.saida?.geral) return (
    <div style={page}>
      <h1 style={pageTitle}>Sprint Retrospectiva</h1>
      <div style={{ color: "#475569", marginTop: 40, textAlign: "center" }}>Sem dados disponíveis.</div>
    </div>
  );

  /* ── Métricas da sprint atual ── */
  const { total, fechado: fechados, aberto: abertos } = resumo.saida.geral;
  const pct = resumo.saida.geral.percentual ?? (total ? Math.round((fechados / total) * 100) : 0);

  const progTotal   = devs.reduce((s, d) => s + (d.programado?.total   || 0), 0);
  const progFechado = devs.reduce((s, d) => s + (d.programado?.fechado || 0), 0);
  const progPct     = progTotal ? Math.round((progFechado / progTotal) * 100) : 0;
  const emrgTotal   = devs.reduce((s, d) => s + (d.emergencia?.total   || 0), 0);
  const emrgFechado = devs.reduce((s, d) => s + (d.emergencia?.fechado || 0), 0);
  const emrgPct     = emrgTotal ? Math.round((emrgFechado / emrgTotal) * 100) : 0;

  /* ── Ranking de devs ── */
  const ranked = devs.map(d => {
    const prog = d.programado || { total: 0, fechado: 0, aberto: 0 };
    const emrg = d.emergencia || { total: 0, fechado: 0, aberto: 0 };
    const tot  = prog.total + emrg.total;
    const fech = prog.fechado + emrg.fechado;
    const p    = tot ? Math.round(fech / tot * 100) : 0;
    return { nome: d.nome, tot, fech, aberto: tot - fech, pct: p };
  }).filter(d => d.tot > 0).sort((a, b) => b.pct - a.pct);

  const destaques  = ranked.filter(d => d.pct >= 70);
  const atencao    = ranked.filter(d => d.pct < 40);

  /* ── Comparativo com sprint anterior ──
     Ordena o histórico por código de sprint (YYMMWW numérico)
     e usa a entrada mais recente como "sprint anterior".
     Sem ordenação, a API pode retornar sprints fora de ordem e
     [length-2] pegaria a sprint errada.
  ── */
  const historicoOrdenado = [...historico]
    .map(item => {
      const saida = item.saida?.geral || item.indicadores?.saida?.geral || item.saida || item;
      const label = String(item.sprint || item.versao || item.grupo_entrega || "0");
      const pctH  = saida.percentual ?? (saida.total ? Math.round(((saida.fechado ?? 0) / saida.total) * 100) : 0);
      return { label, pctH };
    })
    .filter(x => !isNaN(parseInt(x.label)))
    .sort((a, b) => parseInt(a.label) - parseInt(b.label));

  // [length-1] = sprint atual (já está no histórico), [length-2] = anterior real
  const anteriorItem = historicoOrdenado.length >= 2
    ? historicoOrdenado[historicoOrdenado.length - 2]
    : null;
  const pctAnterior = anteriorItem?.pctH ?? null;
  const tendencia   = pctAnterior !== null ? pct - pctAnterior : null;

  /* ── Tickets pendentes ── */
  const pendentes = Array.isArray(tickets)
    ? tickets.filter(t => !FECHADOS_STATUS.has(t.status))
    : [];

  /* ── Labels da sprint ── */
  const sprintLabel = sprint?.grupo_entrega || sprint?.versao || "Sprint Atual";
  const dataFinal   = sprint?.data_final
    ? new Date(sprint.data_final.replace(" ", "T")).toLocaleDateString("pt-BR")
    : null;

  return (
    <div style={page}>

      {/* ── Header ── */}
      <div>
        <h1 style={pageTitle}>Sprint Retrospectiva</h1>
        <div style={pageSub}>
          {sprintLabel}{dataFinal && ` · Encerramento: ${dataFinal}`}
        </div>
      </div>

      {/* ── Resumo geral ── */}
      <div style={kpiGrid}>
        <KpiCard label="Total de tickets" value={total}    cor="#60a5fa" />
        <KpiCard label="Entregues"        value={fechados}  cor="#4ade80" />
        <KpiCard label="Pendentes"        value={abertos}   cor="#f87171" />
        <KpiCard label="Taxa de entrega"  value={`${pct}%`} cor={devColor(pct)} big />
      </div>

      {/* ── Programados vs Emergências ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <SplitCard
          label="Programados"  icon="◎" color="#22d3ee"
          total={progTotal}    fechado={progFechado}  pct={progPct}
        />
        <SplitCard
          label="Emergências"  icon="⚡" color="#a78bfa"
          total={emrgTotal}    fechado={emrgFechado}  pct={emrgPct}
        />
      </div>

      {/* ── Tendência ── */}
      {tendencia !== null && (
        <div style={card}>
          <div style={cardTitle}>Comparativo com Sprint Anterior</div>
          <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>Sprint anterior</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: devColor(pctAnterior) }}>{pctAnterior}%</div>
            </div>
            <div style={{ fontSize: 28, color: "#334155" }}>→</div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>Sprint atual</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: devColor(pct) }}>{pct}%</div>
            </div>
            <div style={{
              marginLeft: "auto", padding: "8px 20px", borderRadius: 10,
              background: tendencia >= 0 ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
              border: `1px solid ${tendencia >= 0 ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: tendencia >= 0 ? "#4ade80" : "#f87171" }}>
                {tendencia >= 0 ? "+" : ""}{tendencia}%
              </div>
              <div style={{ fontSize: 10, color: "#475569", textAlign: "center" }}>
                {tendencia > 5 ? "Melhora" : tendencia >= 0 ? "Estável" : "Queda"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Destaques do time ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

        <div style={card}>
          <div style={cardTitle}>Destaques positivos</div>
          {destaques.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {destaques.map((d, i) => (
                <DevRow key={i} dev={d} pos={i} />
              ))}
            </div>
          ) : (
            <div style={{ color: "#475569", fontSize: 12 }}>Nenhum developer acima de 70% nesta sprint.</div>
          )}
        </div>

        <div style={card}>
          <div style={cardTitle}>Requer atenção</div>
          {atencao.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {atencao.map((d, i) => (
                <DevRow key={i} dev={d} alert />
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#4ade80", fontSize: 12 }}>
              <span>✓</span><span>Todos os developers acima de 40%</span>
            </div>
          )}
        </div>

      </div>

      {/* ── Ranking completo ── */}
      <div style={card}>
        <div style={cardTitle}>Ranking da Sprint</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["#", "Developer", "Total", "Entregues", "Pendentes", "Taxa"].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ranked.map((d, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "rgba(96,165,250,0.03)" : "transparent" }}>
                <td style={td}><span style={{ color: "#475569" }}>{i + 1}</span></td>
                <td style={{ ...td, fontWeight: 700, color: "#e2e8f0" }}>{d.nome}</td>
                <td style={{ ...td, textAlign: "center" }}>{d.tot}</td>
                <td style={{ ...td, textAlign: "center", color: "#4ade80", fontWeight: 700 }}>{d.fech}</td>
                <td style={{ ...td, textAlign: "center", color: "#fbbf24", fontWeight: 700 }}>{d.aberto}</td>
                <td style={{ ...td, textAlign: "center" }}>
                  <span style={{
                    display: "inline-block", padding: "2px 10px", borderRadius: 6,
                    fontSize: 11, fontWeight: 800, color: devColor(d.pct),
                    background: `${devColor(d.pct)}15`, border: `1px solid ${devColor(d.pct)}40`,
                  }}>{d.pct}%</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Tickets pendentes ── */}
      {pendentes.length > 0 && (
        <div style={card}>
          <div style={cardTitle}>Tickets Pendentes — Levar para próxima sprint</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={pillStat}><span style={{ color: "#f87171", fontWeight: 800 }}>{pendentes.length}</span> pendentes</div>
            <div style={pillStat}>
              <span style={{ color: "#22d3ee", fontWeight: 800 }}>
                {pendentes.filter(t => t.programado === "1" || t.programado === 1).length}
              </span> programados
            </div>
            <div style={pillStat}>
              <span style={{ color: "#a78bfa", fontWeight: 800 }}>
                {pendentes.filter(t => !t.programado || t.programado === "0" || t.programado === 0).length}
              </span> emergências
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Ticket", "Título", "Status", "Responsável", "Tipo"].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pendentes.slice(0, 20).map((t, i) => {
                const isProg = t.programado === "1" || t.programado === 1;
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? "rgba(96,165,250,0.03)" : "transparent" }}>
                    <td style={{ ...td, color: "#60a5fa", fontWeight: 700 }}>#{t.id || t.ticketid}</td>
                    <td style={{ ...td, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.subject || t.titulo || "—"}
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{t.status}</span>
                    </td>
                    <td style={{ ...td, color: "#94a3b8" }}>{t.desenvolvedor || t.assigned_to || "—"}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
                        color: isProg ? "#22d3ee" : "#a78bfa",
                        background: isProg ? "rgba(34,211,238,0.1)" : "rgba(167,139,250,0.1)",
                        border: `1px solid ${isProg ? "rgba(34,211,238,0.25)" : "rgba(167,139,250,0.25)"}`,
                      }}>{isProg ? "◎ Programado" : "⚡ Emergência"}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {pendentes.length > 20 && (
            <div style={{ fontSize: 11, color: "#475569", marginTop: 8, textAlign: "center" }}>
              +{pendentes.length - 20} tickets não exibidos
            </div>
          )}
        </div>
      )}

    </div>
  );
}

/* ─── Sub-componentes ─────────────────────────────────────────────── */

function KpiCard({ label, value, cor, big }) {
  return (
    <div style={kpiCard}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ fontSize: big ? 32 : 26, fontWeight: 900, color: cor, lineHeight: 1, marginTop: 6 }}>{value}</div>
    </div>
  );
}

function SplitCard({ label, icon, color, total, fechado, pct }) {
  const aberto = total - fechado;
  return (
    <div style={{ ...card, borderColor: `${color}25` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 16, color }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</span>
        <span style={{ marginLeft: "auto", fontSize: 20, fontWeight: 800, color: devColor(pct) }}>{pct}%</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
        {[["Total", total, "#94a3b8"], ["Entregues", fechado, "#4ade80"], ["Pendentes", aberto, "#f87171"]].map(([l, v, c]) => (
          <div key={l} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: c }}>{v}</div>
            <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 3, transition: "width 0.6s" }} />
      </div>
    </div>
  );
}

function DevRow({ dev: d, pos, alert }) {
  const c = devColor(d.pct);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 10px", borderRadius: 8,
      background: alert ? "rgba(248,113,113,0.05)" : "rgba(74,222,128,0.05)",
      border: `1px solid ${alert ? "rgba(248,113,113,0.15)" : "rgba(74,222,128,0.12)"}`,
    }}>
      {!alert && pos === 0 && <span style={{ fontSize: 16 }}>🥇</span>}
      {!alert && pos === 1 && <span style={{ fontSize: 16 }}>🥈</span>}
      {!alert && pos >= 2 && <span style={{ fontSize: 13, color: "#475569", fontWeight: 700 }}>#{pos + 1}</span>}
      {alert && <span style={{ color: "#f87171" }}>⚠</span>}
      <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{d.nome}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color: c }}>{d.pct}%</span>
      <span style={{ fontSize: 11, color: "#475569" }}>{d.fech}/{d.tot}</span>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const page     = { padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 };
const pageTitle = { color: "#f1f5f9", fontSize: 22, fontWeight: 700, margin: 0 };
const pageSub   = { color: "#475569", fontSize: 13, marginTop: 4 };
const kpiGrid   = { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 };
const kpiCard   = {
  background: "rgba(11,22,42,0.6)", border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 12, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 4,
};
const card = {
  background: "rgba(11,22,42,0.6)", border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14, padding: "20px 22px",
};
const cardTitle = { fontSize: 11, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 16 };
const th = { padding: "8px 12px", fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, textAlign: "left", borderBottom: "1px solid rgba(96,165,250,0.1)" };
const td = { padding: "10px 12px", fontSize: 12, color: "#94a3b8", borderBottom: "1px solid rgba(96,165,250,0.05)" };
const pillStat = { padding: "4px 12px", borderRadius: 20, background: "rgba(96,165,250,0.07)", border: "1px solid rgba(96,165,250,0.12)", fontSize: 12, color: "#94a3b8" };
